import { useState, useEffect } from 'react'
import { useDocumentUrl } from '@/hooks/useDocuments'
import type { Document } from '@/types'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ZoomIn, ZoomOut, RotateCw, Download, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'

interface DocumentViewerProps {
  document: Document | null
}

export function DocumentViewer({ document }: DocumentViewerProps) {
  const [zoom, setZoom] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)

  const { data: signedUrl, isLoading } = useDocumentUrl(document?.storage_path)

  // Reset view when document changes
  useEffect(() => {
    setZoom(100)
    setRotation(0)
    setCurrentPage(1)
  }, [document?.id])

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 200))
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 50))
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360)

  const handleDownload = () => {
    if (signedUrl && document) {
      const link = window.document.createElement('a')
      link.href = signedUrl
      link.download = document.file_name
      link.click()
    }
  }

  const handleOpenExternal = () => {
    if (signedUrl) {
      window.open(signedUrl, '_blank')
    }
  }

  if (!document) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30">
        <p className="text-muted-foreground">Select a document to view</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b p-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <Skeleton className="h-[80%] w-[80%]" />
        </div>
      </div>
    )
  }

  const isPdf = document.file_type === 'application/pdf'
  const isImage = document.file_type.startsWith('image/')

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b p-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate max-w-[200px]">
            {document.file_name}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {isImage && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 50}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs w-12 text-center">{zoom}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 200}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleRotate}>
                <RotateCw className="h-4 w-4" />
              </Button>
              <div className="mx-2 h-4 w-px bg-border" />
            </>
          )}
          <Button variant="ghost" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleOpenExternal}>
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Document View */}
      <div className="flex-1 overflow-auto bg-muted/30 p-4">
        {isPdf && signedUrl && (
          <div className="h-full w-full">
            <iframe
              src={`${signedUrl}#toolbar=0&navpanes=0`}
              className="h-full w-full rounded border bg-white"
              title={document.file_name}
            />
          </div>
        )}

        {isImage && signedUrl && (
          <div className="flex h-full items-center justify-center">
            <img
              src={signedUrl}
              alt={document.file_name}
              className="max-h-full max-w-full object-contain transition-transform"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`
              }}
            />
          </div>
        )}

        {!isPdf && !isImage && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground">
                Preview not available for this file type
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleDownload}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
